<script setup lang="ts">
import { computed } from 'vue'
import type { Block } from '../../models/index.ts'
import BlockTitle from './fields/BlockTitle.vue'
import BlockSubtitle from './fields/BlockSubtitle.vue'
import BlockDescription from './fields/BlockDescription.vue'
import BlockMedia from './fields/BlockMedia.vue'
import QuotationImage from '@/assets/quotation.svg'
import OurTeamPageIcon from '@/assets/OurTeamPageIcon.svg'
import { mainBackgroundClasses } from '@/constants.ts'

const props = defineProps<{
  blockData: Block
  isHeaderBlock: boolean // True if this is the first Block on the page
  customClass?: string
}>()

const backgroundImageList = props.blockData.media ?? []
const media = backgroundImageList[0]

// Used to set custom backgrounds
const backgroundClasses = () => mainBackgroundClasses(props.blockData.backgroundColour)

const customClasses = () => {
  if (props.isHeaderBlock) {
    return props.customClass + ' justify-center items-center'
  } else {
    return props.customClass + ' self-start shadow-md shadow-gray-500'
  }
}

const isCaseExperience = computed(() => props.blockData.blockId?.includes('experience') === true)

const subtitleClass = () => {
  const base = [
    'mt-4 w-full',

    props.isHeaderBlock
      ? 'text-xl font-extralight font-recoleta pl-8'
      : 'rounded-b-xl py-3 font-alexandria',

    !props.isHeaderBlock &&
      (isCaseExperience.value ? 'px-10 text-lg font-normal' : 'px-2 text-xl font-semibold'),
  ].join(' ')

  return [
    base,
    isCaseExperience.value ? backgroundClasses() : props.isHeaderBlock ? '' : 'bg-gray-100',
  ].join(' ')
}

const subTitle = props.isHeaderBlock
  ? '- ' + props.blockData.subtitle
  : (props.blockData.subtitle ?? '')

const descriptionClass = [
  'px-2 pb-3',
  isCaseExperience.value
    ? 'flex items-center justify-center px-10 min-h-[240px] h-auto font-normal text-xl'
    : '',
].join(' ')
</script>

<template>
  <div
    class="flex flex-col w-full"
    :class="[isCaseExperience ? 'bg-white' : backgroundClasses(), customClasses()]"
  >
    <div
      :class="{
        'place-center mt-40 text-center w-4/5 lg:w-1/2': isHeaderBlock,
      }"
    >
      <img
        v-if="blockData.blockId === 'team-quotation'"
        :src="OurTeamPageIcon"
        class="mx-auto w-10"
      />
      <div class="block-media-wrapper">
        <BlockMedia :media="media" :customClass="'w-full'" />
      </div>

      <!-- Block content -->
      <div class="rounded-b-xl">
        <BlockTitle
          :text="blockData.title ?? ''"
          size="xl"
          :custom-class="isHeaderBlock ? 'font-thin mt-4' : 'mt-4'"
        />
        <div
          :id="blockData.blockId"
          class="relative"
          :class="
            isHeaderBlock
              ? 'mt-10 pt-16 text-2xl leading-10 bg-[#CBCBCB] rounded-md p-8 border-b-8 w-170 mx-auto'
              : 'mt-4 text-2xl font-alexandria'
          "
        >
          <img
            v-if="isHeaderBlock"
            class="w-20 h-20 -top-10 left-10 absolute"
            :src="QuotationImage"
            alt="Quotation Image"
          />
          <BlockDescription :custom-class="descriptionClass" :content="blockData.description" />

          <BlockSubtitle :text="subTitle" :custom-class="subtitleClass()" />
        </div>
      </div>
    </div>
  </div>
</template>

<style>
#team-quotation {
  width: 100%;
}

#team-quotation p {
  font-family: var(--font-recoleta);
  font-weight: 400;
}
</style>
